import { readonly, ref, type Ref } from "vue";
import { browserStorage, type ReadableStorage, type WritableStorage } from "./storage.ts";

/**
 * The colour-scheme choice: auto, light or dark (ADR-076, packages/tokens/README.md "Light,
 * dark, auto"). Same contract as the website (apps/astro/src/utils/theme/theme-preference.ts):
 * "auto" is never stored and never written to the page, `<html>` then carries no `data-theme`
 * and the brand tokens follow the system. An explicit choice is stored in localStorage and set
 * as `data-theme`.
 *
 * public/theme-init.js repeats the read and apply steps before the first paint; keep both in
 * step.
 */

export type ThemePreference = "auto" | "light" | "dark";

/** The order the picker shows the choices in. */
export const THEME_PREFERENCES: readonly ThemePreference[] = ["auto", "light", "dark"];

/** localStorage key of an explicit choice, the same as on the website. */
export const THEME_STORAGE_KEY = "theme";

/** "light" and "dark" pass, anything else is "auto". */
export function parseThemePreference(value: unknown): ThemePreference {
  return value === "light" || value === "dark" ? value : "auto";
}

/** The stored choice; missing, blocked or unknown storage means "auto". */
export function readThemePreference(storage: ReadableStorage | null): ThemePreference {
  try {
    return parseThemePreference(storage?.getItem(THEME_STORAGE_KEY));
  } catch {
    return "auto";
  }
}

/** Stores an explicit choice; "auto" removes the key. A blocked storage is ignored. */
export function writeThemePreference(storage: WritableStorage | null, preference: ThemePreference): void {
  try {
    if (preference === "auto") storage?.removeItem(THEME_STORAGE_KEY);
    else storage?.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Storage unavailable: the choice lasts for this page only.
  }
}

/** Sets `data-theme` on the root element, or removes it for "auto". */
export function applyThemePreference(root: HTMLElement, preference: ThemePreference): void {
  if (preference === "auto") delete root.dataset.theme;
  else root.dataset.theme = preference;
}

export type ThemeState = {
  preference: Readonly<Ref<ThemePreference>>;
  setPreference: (preference: ThemePreference) => void;
};

/** The choice as reactive state, read once from storage; every change is stored and applied. */
export function createThemeState(storage: (ReadableStorage & WritableStorage) | null, root: HTMLElement): ThemeState {
  const preference = ref<ThemePreference>(readThemePreference(storage));
  return {
    preference: readonly(preference),
    setPreference(next) {
      preference.value = next;
      writeThemePreference(storage, next);
      applyThemePreference(root, next);
    },
  };
}

let shared: ThemeState | null = null;

/**
 * The one theme state of the page. User menu and footer both show a picker and must agree, so
 * this is the only piece of browser state shared across components (ADR-076 point 3).
 */
export function useTheme(): ThemeState {
  shared ??= createThemeState(browserStorage(), document.documentElement);
  return shared;
}
